import { ApolloProvider, useReactiveVar } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { DARK } from "@dmm-com/rocket-web-ui-library";
import { Global, ThemeProvider } from "@emotion/react";
import type { AppProps } from "next/app";
import { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { useRouter } from "next/router";
import { ErrorDialog } from "~/components/blocks/common/error-dialog";
import { ForeignRedirectWrapper } from "~/components/templates/common/foreign-redirect-wrapper";
import { Layout } from "~/components/templates/layout";
import { PATHS } from "~/const/paths";
import { useClient } from "~/hooks/use-client";
import { useReloadOnBFCache } from "~/hooks/use-reload-on-bfcache";
import { useApolloClient } from "~/lib/apollo/client";
import { Tracking } from "~/lib/tracking";
import DefaultHead from "~/pages/head";
import { AuthProvider, isLoadingAuthVar } from "~/provider/auth";
import { GLOBAL_STYLE } from "~/styles/global-style";
import { initialize } from "~/util/large-small-dynamic-viewport-units-polyfill";

type PageProps = {
  withLayout?: boolean;
};

export type AppPageProps = Omit<AppProps<PageProps>, "pageProps"> & {
  pageProps: PageProps;
};

if (process.env.BUILD_MODE !== "local" && (process.env.SHA || process.env.TAG)) {
  datadogRum.init({
    applicationId: process.env.DATADOG_APP_ID as string,
    clientToken: process.env.DATADOG_CLIENT_TOKEN as string,
    site: "datadoghq.com",
    service: "rocket-web-browser",
    version: process.env.TAG || `${process.env.BUILD_MODE}-v${process.env.SHA}`,
    env: process.env.BUILD_MODE,
    sampleRate: 80,
    sessionReplaySampleRate: 20,
    trackInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingOrigins: [/https:\/\/.*\.tv\.dmm\.com/],
  });
  datadogRum.startSessionReplayRecording();
}

export const visitorUuid = uuidv4();

function MyApp({ Component, pageProps }: AppPageProps) {
  const client = useApolloClient();
  const isClient = useClient();
  const isLoadingAuth = useReactiveVar(isLoadingAuthVar);
  const { asPath } = useRouter();
  const isMaintenancePage = asPath.startsWith(PATHS.MAINTENANCE);

  useReloadOnBFCache();
  useEffect(() => {
    initialize();
  }, []);

  if (!isClient) return null;

  return (
    <ApolloProvider client={client}>
      <ThemeProvider theme={DARK}>
        <Global styles={GLOBAL_STYLE} />
        <DefaultHead />
        <ErrorDialog />
        {isMaintenancePage ? (
          <Component {...pageProps} />
        ) : (
          <ForeignRedirectWrapper>
            <AuthProvider>
              {(() => {
                if (isLoadingAuth) return null;
                return (
                  <Layout>
                    <Component {...pageProps} />
                  </Layout>
                );
              })()}
              <Tracking />
            </AuthProvider>
          </ForeignRedirectWrapper>
        )}
      </ThemeProvider>
    </ApolloProvider>
  );
}

export default MyApp;
